import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import contributions from '../data/contributions';
import './SectionNav.css';

const SectionNav = ({ t, scrollToSection }) => {
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const navRef = useRef(null);

  // Order = order on the page.
  const items = [
    { id: 'about', label: t.nav.about }, // "About" / "O meni"
    { id: 'skills', label: t.skills.title }, // "Tech Stack" / "Tehnologije"
    { id: 'tools', label: t.nav.tools },
    { id: 'featured-projects', label: t.nav.featured },
    { id: 'other-projects', label: t.nav.otherProjects },
    // Only shown once there is at least one contribution (the section itself is hidden until then).
    ...(contributions.length > 0
      ? [{ id: 'contributions', label: t.nav.contributions }]
      : []),
    { id: 'contact', label: t.nav.contact },
  ];

  // Scroll-spy: a section is "active" while it crosses the middle band of the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          } else {
            setActive((current) => (current === entry.target.id ? null : current));
          }
        });
      },
      { rootMargin: '-45% 0px -50% 0px' }
    );

    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  // Close the mobile dropdown on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (id) => {
    // Featured/other subsections disappear when the project search filters them out;
    // fall back to the projects section instead of doing nothing.
    scrollToSection(document.getElementById(id) ? id : 'projects');
    setOpen(false);
  };

  const renderButton = ({ id, label }) => (
    <button
      key={id}
      type="button"
      className={`section-nav-btn ${active === id ? 'active' : ''}`}
      aria-current={active === id ? 'true' : undefined}
      onClick={() => go(id)}
    >
      {label}
    </button>
  );

  const buttons = items.map(renderButton);

  return (
    <motion.nav
      ref={navRef}
      className="section-nav"
      aria-label="Sections"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.5 }}
    >
      {/* Desktop: two-row pill (first 4 items on top, the rest below) */}
      <div className="section-nav-pill">
        <div className="section-nav-row">{items.slice(0, 4).map(renderButton)}</div>
        <div className="section-nav-row">{items.slice(4).map(renderButton)}</div>
      </div>

      {/* Narrow screens: menu button + dropdown */}
      <button
        type="button"
        className="section-nav-toggle"
        aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <FaTimes /> : <FaBars />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="section-nav-dropdown"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {buttons}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default SectionNav;
